import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, ClipboardList, Clock, CheckCircle2, XCircle, Wrench, Bookmark } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { useTableExport } from '@/hooks/useTableExport';
import { useSavedFilters } from '@/hooks/useSavedFilters';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApprovalRecord {
  approver: string;
  decision: 'approved' | 'rejected' | 'pending';
  timestamp?: string;
  comments?: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

interface ChangeRequest {
  id: string;
  changeId: string;
  title: string;
  category: string;
  initiator: string;
  status: string;
  riskLevel: string;
  submittedDate: string;
  targetDate: string;
  description?: string;
  justification?: string;
  affectedAreas?: string;
  approvalHistory?: ApprovalRecord[];
  implementationChecklist?: ChecklistItem[];
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const MOCK_CHANGES: ChangeRequest[] = [
  {
    id: '1', changeId: 'CCR-0041', title: 'Update HPLC Column Brand for API Purity Testing', category: 'equipment', initiator: 'Priya Sharma', status: 'approved', riskLevel: 'medium', submittedDate: '2024-03-01T10:00:00Z', targetDate: '2024-04-15T00:00:00Z',
    description: 'Replacement of Agilent Zorbax C18 with Waters Acquity BEH C18 column due to supplier discontinuation. Equivalence study confirms comparable performance across all test methods.',
    justification: 'Agilent has discontinued the current column model. Alternative validated during bridging study (ref: VS-2024-003). No impact on analytical results expected.',
    affectedAreas: 'QC Lab 1, QC Lab 2, Analytical Method AM-HPL-005',
    approvalHistory: [
      { approver: 'QA Manager – Suresh Nair', decision: 'approved', timestamp: '2024-03-05T14:00:00Z', comments: 'Equivalence data reviewed and accepted.' },
      { approver: 'Lab Head – Dr. Anita Rao', decision: 'approved', timestamp: '2024-03-07T09:30:00Z', comments: 'Proceed. Update all relevant SOPs.' },
    ],
    implementationChecklist: [
      { id: 'ic1', label: 'Equivalence study completed', done: true },
      { id: 'ic2', label: 'SOP AM-HPL-005 revised', done: true },
      { id: 'ic3', label: 'Staff training on new column', done: false },
      { id: 'ic4', label: 'Validation report filed in DMS', done: false },
    ],
  },
  {
    id: '2', changeId: 'CCR-0042', title: 'Change in Granulation Process Parameters for Product P-107', category: 'process', initiator: 'Anil Verma', status: 'pending', riskLevel: 'high', submittedDate: '2024-03-10T08:00:00Z', targetDate: '2024-05-01T00:00:00Z',
    description: 'Proposed change in wet granulation mixer speed from 150 RPM to 180 RPM and granulation time from 8 min to 6 min to reduce cycle time and improve granule uniformity.',
    justification: 'Scale-up trials at pilot batch level showed improved CQA scores. Formal process validation to follow.',
    affectedAreas: 'Manufacturing Suite A, BMR P-107, Batch Release',
    approvalHistory: [
      { approver: 'QA Manager – Suresh Nair', decision: 'pending' },
    ],
    implementationChecklist: [
      { id: 'ic1', label: 'Pilot batch data reviewed', done: true },
      { id: 'ic2', label: 'Risk assessment completed', done: true },
      { id: 'ic3', label: 'QA approval received', done: false },
      { id: 'ic4', label: 'BMR updated', done: false },
      { id: 'ic5', label: 'Process validation completed', done: false },
    ],
  },
  {
    id: '3', changeId: 'CCR-0043', title: 'Introduce New CRM Supplier for Reference Standards', category: 'material', initiator: 'Kavya Menon', status: 'draft', riskLevel: 'low', submittedDate: '2024-03-12T11:00:00Z', targetDate: '2024-04-30T00:00:00Z',
    description: 'Qualification of Sigma-Aldrich as secondary CRM supplier for working reference standards to ensure supply chain continuity.',
    justification: 'Current sole-source dependency creates risk. Sigma-Aldrich materials have been informally benchmarked with satisfactory results.',
    affectedAreas: 'QC Reference Standards Store, Analytical QC',
    approvalHistory: [],
    implementationChecklist: [],
  },
  {
    id: '4', changeId: 'CCR-0044', title: 'SOP Revision – Environmental Monitoring Frequency', category: 'document', initiator: 'Meena Pillai', status: 'implemented', riskLevel: 'low', submittedDate: '2024-02-20T09:00:00Z', targetDate: '2024-03-20T00:00:00Z',
    description: 'Increase environmental monitoring frequency in classified areas from monthly to bi-weekly per latest GMP guidance update.',
    justification: 'Regulatory inspection observation (ref: INS-2024-012). Proactive compliance action.',
    affectedAreas: 'Microbiology Lab, Cleanroom A, Cleanroom B',
    approvalHistory: [
      { approver: 'QA Manager – Suresh Nair', decision: 'approved', timestamp: '2024-02-25T10:00:00Z', comments: 'Align with new GMP annex guidance.' },
    ],
    implementationChecklist: [
      { id: 'ic1', label: 'SOP EM-001 revised and approved', done: true },
      { id: 'ic2', label: 'Staff notified', done: true },
      { id: 'ic3', label: 'Monitoring schedule updated', done: true },
    ],
  },
  {
    id: '5', changeId: 'CCR-0045', title: 'ERP System Module Upgrade – QC Test Results Interface', category: 'system', initiator: 'Farhan Khan', status: 'rejected', riskLevel: 'critical', submittedDate: '2024-03-05T13:00:00Z', targetDate: '2024-06-01T00:00:00Z',
    description: 'Proposed upgrade of QC results module from ERP v5.2 to v6.0 with new AI-assisted OOS flag feature.',
    justification: 'Vendor will end support for v5.2 in Q4 2024.',
    affectedAreas: 'All QC labs, Batch release workflow',
    approvalHistory: [
      { approver: 'IT Security Officer', decision: 'rejected', timestamp: '2024-03-08T11:00:00Z', comments: 'Validation package insufficient. Computer system validation (CSV) not initiated. Resubmit with full GAMP 5 risk assessment and IQ/OQ/PQ plan.' },
    ],
    implementationChecklist: [],
  },
];

function safeDate(val: unknown, fmt: string): string {
  try {
    const d = new Date(String(val ?? ''));
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt);
  } catch {
    return '—';
  }
}

const STATUS_OPTIONS = ['draft', 'pending', 'approved', 'rejected', 'implemented', 'closed'];
const CATEGORY_OPTIONS = ['process', 'equipment', 'material', 'document', 'system', 'facility'];
const RISK_OPTIONS = ['low', 'medium', 'high', 'critical'];

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ChangeControl() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<ChangeRequest | null>(null);
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [form, setForm] = useState({
    title: '',
    category: '',
    riskLevel: 'medium',
    description: '',
    justification: '',
    targetDate: '',
    affectedAreas: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['change-control', { search, status: statusFilter, category: categoryFilter, risk: riskFilter, page }],
    queryFn: () =>
      api.get('/change-control', { params: { search, status: statusFilter, category: categoryFilter, risk: riskFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_CHANGES, meta: { total: MOCK_CHANGES.length } })),
  });

  const rows: ChangeRequest[] = data?.data ?? MOCK_CHANGES;
  const total: number = data?.meta?.total ?? MOCK_CHANGES.length;

  const filtered = rows.filter((c) => {
    const matchSearch = !search || c.changeId.toLowerCase().includes(search.toLowerCase()) || c.title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || c.status === statusFilter;
    const matchCategory = !categoryFilter || c.category === categoryFilter;
    const matchRisk = !riskFilter || c.riskLevel === riskFilter;
    return matchSearch && matchStatus && matchCategory && matchRisk;
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/change-control', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-control'] });
      setShowCreate(false);
      setForm({ title: '', category: '', riskLevel: 'medium', description: '', justification: '', targetDate: '', affectedAreas: '' });
    },
  });

  const exportColumns = [
    { key: 'changeId', label: 'Change ID' },
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'initiator', label: 'Initiator' },
    { key: 'status', label: 'Status' },
    { key: 'riskLevel', label: 'Risk Level' },
    { key: 'submittedDate', label: 'Submitted Date' },
    { key: 'targetDate', label: 'Target Date' },
  ];
  const { exportCSV, exportPDF } = useTableExport(filtered as unknown as Record<string, unknown>[], exportColumns, 'change-control-log');

  const { saved: savedFilters, saveFilter, loadFilter, deleteFilter } = useSavedFilters('change-control');

  const stats = {
    total: filtered.length,
    pending: filtered.filter((c) => c.status === 'pending').length,
    approved: filtered.filter((c) => c.status === 'approved').length,
    rejected: filtered.filter((c) => c.status === 'rejected').length,
    implemented: filtered.filter((c) => c.status === 'implemented').length,
  };

  const riskColors: Record<string, string> = {
    low: '#16A34A', medium: '#D97706', high: '#D97706', critical: '#DC2626',
  };

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'changeId', label: 'Change ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs font-semibold text-teal-700">{String(row.changeId)}</span>,
    },
    {
      key: 'title', label: 'Title / Category',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm truncate max-w-xs">{String(row.title)}</p>
          <p className="text-xs text-text-dim capitalize">{String(row.category)} · {String(row.initiator)}</p>
        </div>
      ),
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'riskLevel', label: 'Risk', width: 'w-24',
      render: (row) => {
        const r = String(row.riskLevel);
        return (
          <span className="text-xs font-semibold capitalize px-2 py-0.5 rounded-full" style={{ background: `${riskColors[r] || '#64748b'}18`, color: riskColors[r] || '#64748b' }}>
            {r}
          </span>
        );
      },
    },
    {
      key: 'submittedDate', label: 'Submitted', width: 'w-28',
      render: (row) => <span className="text-xs font-mono text-text-muted">{safeDate(row.submittedDate, 'd MMM yyyy')}</span>,
    },
    {
      key: 'targetDate', label: 'Target', width: 'w-28',
      render: (row) => <span className="text-xs font-mono text-text-muted">{safeDate(row.targetDate, 'd MMM yyyy')}</span>,
    },
  ];

  const handleApplyFilter = (id: string) => {
    const f = loadFilter(id);
    if (!f) return;
    if (f.status !== undefined) setStatusFilter(f.status);
    if (f.category !== undefined) setCategoryFilter(f.category);
    if (f.risk !== undefined) setRiskFilter(f.risk);
    if (f.search !== undefined) setSearch(f.search);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Change Control Log"
        subtitle="All lab changes require documented approval"
        actions={
          <>
            <button onClick={exportPDF} className="btn-ghost"><FileText size={13} /> PDF</button>
            <button onClick={exportCSV} className="btn-ghost"><Download size={13} /> CSV</button>
            <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> New Change Request</button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Changes" value={stats.total} icon={ClipboardList} color="#b07d1a" />
        <StatCard label="Pending Approval" value={stats.pending} icon={Clock} color="#D97706" />
        <StatCard label="Approved" value={stats.approved} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="Rejected" value={stats.rejected} icon={XCircle} color="#DC2626" />
        <StatCard label="Implemented" value={stats.implemented} icon={Wrench} color="#2563EB" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by Change ID or title..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>

        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>

        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
        </select>

        <select value={riskFilter} onChange={(e) => { setRiskFilter(e.target.value); setPage(1); }} className="select w-32">
          <option value="">All Risk</option>
          {RISK_OPTIONS.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
        </select>

        {/* Saved Filters */}
        <div className="relative">
          <button onClick={() => setShowSaveFilter(!showSaveFilter)} className="btn-ghost">
            <Bookmark size={13} /> Saved Filters
            {savedFilters.length > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-teal text-white text-[9px] flex items-center justify-center">{savedFilters.length}</span>
            )}
          </button>
          {showSaveFilter && (
            <div className="absolute right-0 top-10 z-30 card p-3 w-60 space-y-2 shadow-xl">
              <p className="text-xs font-semibold text-text-muted">Saved Filters</p>
              {savedFilters.length === 0 && <p className="text-xs text-text-dim">No saved filters yet.</p>}
              {savedFilters.map((f) => (
                <div key={f.id} className="flex items-center justify-between group">
                  <button onClick={() => { handleApplyFilter(f.id); setShowSaveFilter(false); }} className="text-xs text-text hover:text-teal">{f.name}</button>
                  <button onClick={() => deleteFilter(f.id)} className="text-[10px] text-red-500 opacity-0 group-hover:opacity-100">remove</button>
                </div>
              ))}
              <div className="border-t border-border pt-2 flex gap-1">
                <input className="input text-xs flex-1 py-1" placeholder="Filter name..." value={saveFilterName} onChange={(e) => setSaveFilterName(e.target.value)} />
                <button
                  onClick={() => {
                    if (saveFilterName) {
                      saveFilter(saveFilterName, { status: statusFilter, category: categoryFilter, risk: riskFilter, search });
                      setSaveFilterName('');
                    }
                  }}
                  className="btn-primary text-xs py-1 px-2"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelected(row as unknown as ChangeRequest)}
        total={total}
        page={page}
        onPageChange={setPage}
      />

      {/* New Change Request Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Change Request"
        subtitle="Document and track this change through the approval workflow"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Submitting...' : 'Submit for Review'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Change Title *</label>
            <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Brief descriptive title..." />
          </div>
          <div>
            <label className="label">Category *</label>
            <select className="select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              <option value="">Select category...</option>
              {CATEGORY_OPTIONS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Risk Level *</label>
            <select className="select" value={form.riskLevel} onChange={(e) => setForm((f) => ({ ...f, riskLevel: e.target.value }))}>
              {RISK_OPTIONS.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Target Implementation Date *</label>
            <input type="date" className="input" value={form.targetDate} onChange={(e) => setForm((f) => ({ ...f, targetDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Affected Areas</label>
            <input className="input" value={form.affectedAreas} onChange={(e) => setForm((f) => ({ ...f, affectedAreas: e.target.value }))} placeholder="Labs, processes, documents..." />
          </div>
          <div className="col-span-2">
            <label className="label">Description of Change *</label>
            <textarea className="input resize-none" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What is being changed and how..." />
          </div>
          <div className="col-span-2">
            <label className="label">Justification / Business Rationale *</label>
            <textarea className="input resize-none" rows={3} value={form.justification} onChange={(e) => setForm((f) => ({ ...f, justification: e.target.value }))} placeholder="Why is this change necessary..." />
          </div>
        </div>
      </Modal>

      {/* Change Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.changeId || ''}
        subtitle={selected?.title}
        width={540}
      >
        {selected && (
          <div className="space-y-5">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-bg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Status</p>
                <PillBadge value={selected.status} showDot />
              </div>
              <div className="p-3 rounded-lg bg-bg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Risk</p>
                <span className="text-xs font-semibold capitalize" style={{ color: riskColors[selected.riskLevel] || '#64748b' }}>{selected.riskLevel}</span>
              </div>
              <div className="p-3 rounded-lg bg-bg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Category</p>
                <span className="text-xs font-medium capitalize text-text">{selected.category}</span>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-text-muted mb-1">Description</p>
              <p className="text-sm text-text leading-relaxed p-3 bg-bg rounded-lg">{selected.description || '—'}</p>
            </div>

            <div>
              <p className="text-xs font-semibold text-text-muted mb-1">Justification</p>
              <p className="text-sm text-text leading-relaxed p-3 bg-bg rounded-lg">{selected.justification || '—'}</p>
            </div>

            {selected.affectedAreas && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Affected Areas</p>
                <p className="text-sm text-text-muted p-3 bg-bg rounded-lg">{selected.affectedAreas}</p>
              </div>
            )}

            {/* Approval History */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-3">Approval History</p>
              {(selected.approvalHistory || []).length === 0 ? (
                <p className="text-xs text-text-dim">No approvals recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {selected.approvalHistory!.map((a, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-text">{a.approver}</p>
                        <PillBadge value={a.decision} showDot />
                      </div>
                      {a.timestamp && <p className="text-[11px] text-text-dim">{safeDate(a.timestamp, 'd MMM yyyy HH:mm')}</p>}
                      {a.comments && <p className="text-[11px] text-text-muted mt-1 italic">{a.comments}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Implementation Checklist */}
            {(selected.implementationChecklist || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-3">Implementation Checklist</p>
                <div className="space-y-2">
                  {selected.implementationChecklist!.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                        {item.done ? <CheckCircle2 size={11} className="text-green-600" /> : <div className="w-2 h-2 rounded-sm border border-gray-400" />}
                      </div>
                      <span className={`text-xs ${item.done ? 'text-text-muted line-through' : 'text-text'}`}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pt-2 border-t border-border">
              <div>
                <p className="text-[10px] text-text-dim">Submitted</p>
                <p className="text-xs font-mono text-text-muted">{safeDate(selected.submittedDate, 'd MMM yyyy')}</p>
              </div>
              <div>
                <p className="text-[10px] text-text-dim">Target Date</p>
                <p className="text-xs font-mono text-text-muted">{safeDate(selected.targetDate, 'd MMM yyyy')}</p>
              </div>
              <div>
                <p className="text-[10px] text-text-dim">Initiator</p>
                <p className="text-xs font-medium text-text">{selected.initiator}</p>
              </div>
            </div>
          </div>
        )}
      </SideDrawer>
    </div>
  );
}
